import React, { useEffect, useRef, useState } from 'react'

import type { FormikProps } from 'formik'
import * as yup from 'yup'

import { Alert, AlertIcon, Box, Progress, Stack } from '@chakra-ui/react'
import {
  Typography,
  Button,
  Form,
  Select,
  UploadFile,
  Modal,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalFooter,
  useToast,
} from '@pnld/components-web'

import { checkUploadReady, downloadTemplate } from '@/api/processing/services'
import { SelectionDivergence, ServiceMapError } from '@/api/processing/types'
import { DeedType, EducationStage } from '@/types/processing'
import { getErrorMessage } from '@/utils/api'
import { tusUploadFile } from '@/utils/tusUpload'

import { deedTypeOptions, educationStageOptions } from '../../constants'
import { blockEmptyFileChange } from './emptyFileChangeGuard'
import { buildSubmitPayload } from './payload'
import { NewProcessingModalProps, NewProcessingForm } from './types'
import useSelectionConsistency, {
  SelectionConsistencyStatus,
} from './useSelectionConsistency'
import useServiceMapValidation, {
  ServiceMapStatus,
} from './useServiceMapValidation'

type TemplateType = 'adesao' | 'escolha' | 'mapa_atendimento'

// PSV-424: texto literal do card.
export const SERVICE_MAP_HELP_TEXT =
  'Envie o mapa para definir as etapas de ensino atendidas por cada grupo de ' +
  'coleções. O vínculo com as coleções é realizado pelo sufixo de 8 dígitos ' +
  'do código da coleção. Categoria, Área e Componente são utilizados para ' +
  'identificação e exibição das informações. Caso nenhum mapa seja enviado, ' +
  'o processamento seguirá o fluxo padrão.'

const allowedDeedTypeValues = deedTypeOptions.map(option =>
  String(option.value)
)
const allowedEducationStageValues = educationStageOptions.map(option =>
  String(option.value)
)

type WatchedSelection = Pick<
  NewProcessingForm,
  'nuDeedType' | 'nuEducationStage' | 'selectionReportFile'
>

const INITIAL_SELECTION: WatchedSelection = {
  nuDeedType: null,
  nuEducationStage: null,
  selectionReportFile: null,
}

const INITIAL_VALUES: NewProcessingForm = {
  nuDeedType: null,
  nuEducationStage: null,
  adherenceReportFile: null,
  selectionReportFile: null,
  serviceMapFile: null,
}

type ValidatingState = React.ComponentProps<
  typeof UploadFile
>['validatingState']

const SELECTION_VALIDATING_STATE: Record<
  SelectionConsistencyStatus,
  ValidatingState
> = {
  idle: undefined,
  uploading: 'loading',
  checking: 'loading',
  valid: 'valid',
  divergent: 'invalid',
  error: undefined,
}

const SERVICE_MAP_VALIDATING_STATE: Record<ServiceMapStatus, ValidatingState> =
  {
    idle: undefined,
    uploading: 'loading',
    checking: 'loading',
    valid: 'valid',
    invalid: 'invalid',
    error: undefined,
  }

/**
 * Limpar um `Select` grava string vazia no Formik, não `null` — e `Number('')`
 * é 0, que não existe em `DeedType`/`EducationStage`. Sem esta conversão o
 * modal dispara a conferência com um enum inválido.
 */
const toEnum = <T,>(value: unknown): T | null =>
  value === null || value === undefined || value === ''
    ? null
    : (Number(value) as T)

const validationSchema = yup.object().shape({
  nuDeedType: yup
    .string()
    .oneOf(allowedDeedTypeValues, 'Tipo de obra inválido')
    .required('Tipo de obra é obrigatório'),
  nuEducationStage: yup
    .string()
    .oneOf(allowedEducationStageValues, 'Etapa de ensino inválida')
    .required('Etapa de ensino é obrigatória'),
  adherenceReportFile: yup
    .string()
    .required('Relatório de adesão é obrigatório'),
  selectionReportFile: yup
    .string()
    .required('Relatório de escolha é obrigatório'),
})

/**
 * PSV-352 — espelha em estado o que o Formik tem em mãos. O `Form` do
 * components-web aceita children como render-prop, mas hooks não podem ser
 * chamados lá dentro — daí este observador.
 */
const SelectionWatcher: React.FC<{
  values: NewProcessingForm
  onChange: (selection: WatchedSelection) => void
}> = ({ values, onChange }) => {
  const { nuDeedType, nuEducationStage, selectionReportFile } = values
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Só os três campos que a conferência usa, e não `values` inteiro: o Formik
  // entrega um objeto novo a cada tecla, e reagir a ele dispararia conferência
  // à toa. Com o handler numa ref, as deps ficam completas sem eslint-disable.
  useEffect(() => {
    onChangeRef.current({ nuDeedType, nuEducationStage, selectionReportFile })
  }, [nuDeedType, nuEducationStage, selectionReportFile])

  return null
}

/**
 * PSV-424 — mesmo papel do `SelectionWatcher`, só para o mapa: observa um
 * campo e não `values` inteiro, para não reconferir a cada tecla.
 */
const ServiceMapWatcher: React.FC<{
  file: File | null
  onChange: (file: File | null) => void
}> = ({ file, onChange }) => {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    // Remover no `UploadFile` grava `undefined`; o hook trata falsy igual.
    onChangeRef.current(file ?? null)
  }, [file])

  return null
}

// `brand.neutral.dark_1` (#636363) some no fundo claro do Alert. `dark_2`
// (#333333) é o que o resto do painel usa para texto de corpo.
const DIVERGENCE_TEXT_COLOR = 'brand.neutral.dark_2'

/**
 * PSV-352 — uma divergência por item de lista. A mensagem já vem do backend
 * começando pelo campo e com os dois valores na frase, então aqui basta o
 * marcador. O `pre-line` preserva a segunda linha que o backend isola nos casos
 * de formato inválido e valor fora do domínio.
 */
const DivergenceItem: React.FC<{ divergence: SelectionDivergence }> = ({
  divergence,
}) => (
  <Box display="flex" gap={2} alignItems="flex-start">
    <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR} lineHeight="1.5">
      •
    </Typography>
    <Typography
      variant="b-sm"
      whiteSpace="pre-line"
      color={DIVERGENCE_TEXT_COLOR}
      lineHeight="1.5"
    >
      {divergence.message}
    </Typography>
  </Box>
)

const ServiceMapErrorItem: React.FC<{ error: ServiceMapError }> = ({
  error,
}) => (
  <Box display="flex" gap={2} alignItems="flex-start">
    <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR} lineHeight="1.5">
      •
    </Typography>
    <Typography
      variant="b-sm"
      whiteSpace="pre-line"
      color={DIVERGENCE_TEXT_COLOR}
      lineHeight="1.5"
    >
      {error.message}
    </Typography>
  </Box>
)

// Com 429 não há botão de tentar de novo: antes do prazo, só receberia outro 429.
const THROTTLED_RETRY_HINT = 'Depois desse prazo, anexe o arquivo de novo.'

const ADHERENCE_UPLOAD_ERROR =
  'Não foi possível enviar o Relatório de Adesão. Tente novamente em ' +
  'instantes. Se persistir, avise o suporte.'

const UPLOADS_NOT_READY_ERROR =
  'Não foi possível confirmar que os arquivos chegaram ao servidor. ' +
  'Aguarde alguns instantes e tente novamente.'

const waitUntilReady = async (
  uploadId: string,
  maxAttempts = 10,
  delayMs = 500
): Promise<void> => {
  for (let i = 0; i < maxAttempts; i++) {
    const result = await checkUploadReady(uploadId)
    if (result?.ready) return
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  throw new Error(`Upload ${uploadId} não ficou pronto`)
}

const NewProcessingModal: React.FC<NewProcessingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const toast = useToast()
  const formikRef = useRef<FormikProps<NewProcessingForm> | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [adherenceProgress, setAdherenceProgress] = useState(0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Adesão já enviada: ao tentar de novo, só a conferência é refeita.
  const adherenceUploadRef = useRef<{ file: File; uploadId: string } | null>(
    null
  )
  const [downloadingTemplate, setDownloadingTemplate] =
    useState<TemplateType | null>(null)
  const [watchedValues, setWatchedValues] =
    useState<WatchedSelection>(INITIAL_SELECTION)
  const [serviceMapFile, setServiceMapFile] = useState<File | null>(null)

  const {
    status: consistencyStatus,
    divergences,
    uploadId: selectionUploadId,
    uploadProgress: selectionUploadProgress,
    canSubmit,
    errorDetail: consistencyErrorDetail,
    retry: retryConsistency,
    reset: resetConsistency,
  } = useSelectionConsistency({
    file: watchedValues.selectionReportFile,
    nuDeedType: toEnum<DeedType>(watchedValues.nuDeedType),
    nuEducationStage: toEnum<EducationStage>(watchedValues.nuEducationStage),
  })

  const {
    status: serviceMapStatus,
    errors: serviceMapErrors,
    uploadId: serviceMapUploadId,
    validatedFile: serviceMapValidatedFile,
    errorDetail: serviceMapErrorDetail,
    retry: retryServiceMap,
    reset: resetServiceMap,
  } = useServiceMapValidation({ file: serviceMapFile })

  // PSV-424: com mapa anexado, só `valid` libera. Pelo status também, e não
  // só pelo arquivo: entre a remoção e o próximo render o hook ainda pode
  // estar numa conferência. E o `valid` tem de ser do arquivo que está no
  // campo: logo depois de uma troca, ele ainda é do anterior.
  const serviceMapBlocking =
    serviceMapStatus === 'valid'
      ? serviceMapValidatedFile !== serviceMapFile
      : Boolean(serviceMapFile) || serviceMapStatus !== 'idle'

  const serviceMapValidatingState =
    SERVICE_MAP_VALIDATING_STATE[serviceMapStatus]

  const validatingState = SELECTION_VALIDATING_STATE[consistencyStatus]

  // Reabrir o modal não pode herdar o uploadId da solicitação anterior.
  useEffect(() => {
    if (!isOpen) {
      resetConsistency()
      setWatchedValues(INITIAL_SELECTION)
      resetServiceMap()
      setServiceMapFile(null)
      setSubmitError(null)
      adherenceUploadRef.current = null
    }
  }, [isOpen, resetConsistency, resetServiceMap])

  const handleDownloadTemplate = async (type: TemplateType) => {
    setDownloadingTemplate(type)
    try {
      const blob = await downloadTemplate(type)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `template_${type}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => window.URL.revokeObjectURL(url), 5000)
    } catch {
      toast({
        status: 'error',
        title: 'Erro ao baixar template. Tente novamente.',
      })
    } finally {
      setDownloadingTemplate(null)
    }
  }

  const handleSubmit = async (values: NewProcessingForm) => {
    if (
      values.nuDeedType === null ||
      values.nuEducationStage === null ||
      values.adherenceReportFile === null ||
      values.selectionReportFile === null ||
      !onSubmit
    ) {
      return
    }

    // O relatório de escolha já subiu no anexo; reenviar duplicaria
    // centenas de MB (o teto do campo é 800 MB).
    if (!selectionUploadId || !canSubmit) {
      return
    }

    // PSV-424: o mapa também já subiu no anexo, e só vai conferido — e
    // conferido *este* arquivo. `values` é o que o Formik tem agora; o estado
    // do hook pode ainda ser do mapa anterior (troca) ou de um removido.
    const currentServiceMap = values.serviceMapFile || null
    const hasServiceMap =
      Boolean(currentServiceMap) || serviceMapStatus !== 'idle'
    const serviceMapReady =
      Boolean(currentServiceMap) &&
      serviceMapStatus === 'valid' &&
      Boolean(serviceMapUploadId) &&
      serviceMapValidatedFile === currentServiceMap
    if (hasServiceMap && !serviceMapReady) {
      return
    }

    const previousUpload = adherenceUploadRef.current
    const reusingUpload =
      previousUpload !== null &&
      previousUpload.file === values.adherenceReportFile

    setIsSubmitting(true)
    setIsUploading(!reusingUpload)
    setAdherenceProgress(0)
    setSubmitError(null)

    try {
      let adherenceUploadId: string
      if (reusingUpload) {
        adherenceUploadId = previousUpload.uploadId
      } else {
        try {
          adherenceUploadId = await tusUploadFile(
            values.adherenceReportFile,
            setAdherenceProgress
          )
        } catch (error) {
          // A mensagem da API (ex.: o prazo de um 429) vale mais que a genérica.
          setSubmitError(getErrorMessage(error, ADHERENCE_UPLOAD_ERROR))
          return
        }
        adherenceUploadRef.current = {
          file: values.adherenceReportFile,
          uploadId: adherenceUploadId,
        }
      }

      setIsUploading(false)

      try {
        await Promise.all([
          waitUntilReady(adherenceUploadId),
          waitUntilReady(selectionUploadId),
          ...(hasServiceMap && serviceMapUploadId
            ? [waitUntilReady(serviceMapUploadId)]
            : []),
        ])
      } catch {
        // Reaproveitado e ainda não pronto: a próxima tentativa reenvia.
        if (reusingUpload) adherenceUploadRef.current = null
        setSubmitError(UPLOADS_NOT_READY_ERROR)
        return
      }

      await onSubmit(
        buildSubmitPayload(values, {
          adherenceUploadId,
          selectionUploadId,
          serviceMapUploadId: hasServiceMap ? serviceMapUploadId : null,
        })
      )
    } catch {
      // O onSubmit da página já mostra o erro num toast. O backend pode ter
      // consumido o upload ao falhar, então a próxima tentativa reenvia.
      adherenceUploadRef.current = null
    } finally {
      setIsSubmitting(false)
      setIsUploading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // Formulário mais alto que a tela: só o corpo rola, título e botões ficam.
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <Typography variant="h-xs" color="brand.neutral.dark_2">
            Solicitar novo processamento
          </Typography>
        </ModalHeader>
        <ModalBody>
          <Form
            innerRef={formikRef}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            initialValues={INITIAL_VALUES}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {formik => (
              <Stack gap={1}>
                <SelectionWatcher
                  values={formik.values}
                  onChange={setWatchedValues}
                />
                <ServiceMapWatcher
                  file={formik.values.serviceMapFile}
                  onChange={setServiceMapFile}
                />
                <Select
                  name="nuDeedType"
                  label="Tipos de obras"
                  placeholder="selecione o tipo de obra a ser processada"
                  options={deedTypeOptions}
                  disabled={isSubmitting}
                />
                <Select
                  name="nuEducationStage"
                  label="Etapa de ensino"
                  placeholder="selecione a etapa que deseja processar"
                  options={educationStageOptions}
                  disabled={isSubmitting}
                />
                <Box onChangeCapture={blockEmptyFileChange}>
                  <UploadFile
                    id="adherence-report"
                    name="adherenceReportFile"
                    label="Envio do Relatório de Adesão"
                    accept=".csv,.xlsx"
                    helperText="O arquivo deve estar no formato csv ou xlsx."
                    maxSize={20 * 1024 * 1024}
                    sx={{ '& > :last-child': { margin: 0 } }}
                    disabled={isSubmitting}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="tertiary"
                      label={
                        downloadingTemplate === 'adesao'
                          ? 'Baixando...'
                          : 'Baixar template'
                      }
                      iconName="download"
                      border="pill"
                      onClick={() => handleDownloadTemplate('adesao')}
                      isLoading={downloadingTemplate === 'adesao'}
                      disabled={isSubmitting || downloadingTemplate !== null}
                    />
                  </Box>
                </Box>
                <Box onChangeCapture={blockEmptyFileChange}>
                  <UploadFile
                    id="selection-report"
                    name="selectionReportFile"
                    label="Envio do Relatório de Escolha"
                    accept=".csv,.xlsx"
                    helperText="O arquivo deve estar no formato csv ou xlsx."
                    maxSize={800 * 1024 * 1024}
                    sx={{ '& > :last-child': { margin: 0 } }}
                    disabled={isSubmitting}
                    isLoading={
                      consistencyStatus === 'uploading' ||
                      consistencyStatus === 'checking'
                    }
                    validatingState={validatingState}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="tertiary"
                      label={
                        downloadingTemplate === 'escolha'
                          ? 'Baixando...'
                          : 'Baixar template'
                      }
                      iconName="download"
                      border="pill"
                      onClick={() => handleDownloadTemplate('escolha')}
                      isLoading={downloadingTemplate === 'escolha'}
                      disabled={isSubmitting || downloadingTemplate !== null}
                    />
                  </Box>
                </Box>

                <Box
                  onChangeCapture={blockEmptyFileChange}
                  data-testid="service-map-field"
                >
                  <UploadFile
                    id="service-map"
                    name="serviceMapFile"
                    label="Mapa de Atendimento (opcional)"
                    accept=".csv,.xlsx"
                    helperText="O arquivo deve estar no formato csv ou xlsx."
                    maxSize={10 * 1024 * 1024}
                    sx={{ '& > :last-child': { margin: 0 } }}
                    disabled={isSubmitting}
                    isLoading={
                      serviceMapStatus === 'uploading' ||
                      serviceMapStatus === 'checking'
                    }
                    validatingState={serviceMapValidatingState}
                  />
                  <Typography
                    variant="b-sm"
                    color={DIVERGENCE_TEXT_COLOR}
                    mt={1}
                  >
                    {SERVICE_MAP_HELP_TEXT}
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="tertiary"
                      label={
                        downloadingTemplate === 'mapa_atendimento'
                          ? 'Baixando...'
                          : 'Baixar template'
                      }
                      iconName="download"
                      border="pill"
                      onClick={() => handleDownloadTemplate('mapa_atendimento')}
                      isLoading={downloadingTemplate === 'mapa_atendimento'}
                      disabled={isSubmitting || downloadingTemplate !== null}
                    />
                  </Box>
                </Box>

                {consistencyStatus === 'uploading' && (
                  <Stack gap={1} mt={2}>
                    <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR}>
                      Enviando o Relatório de Escolha para conferência —{' '}
                      {selectionUploadProgress}%
                    </Typography>
                    <Progress
                      value={selectionUploadProgress}
                      colorScheme="blue"
                      hasStripe
                      isAnimated
                      borderRadius="full"
                      size="sm"
                    />
                  </Stack>
                )}

                {consistencyStatus === 'checking' && (
                  <Alert status="info" borderRadius="md" mt={2}>
                    <AlertIcon />
                    <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR}>
                      Conferindo o relatório de escolha com a etapa de ensino e
                      o tipo de obra selecionados...
                    </Typography>
                  </Alert>
                )}

                {consistencyStatus === 'divergent' && (
                  <Alert
                    status="error"
                    borderRadius="md"
                    mt={2}
                    alignItems="flex-start"
                    data-testid="selection-divergences"
                  >
                    <AlertIcon />
                    <Stack gap={1} w="100%">
                      <Typography variant="s-sm" color={DIVERGENCE_TEXT_COLOR}>
                        O relatório de escolha não corresponde à seleção feita
                        acima.
                      </Typography>
                      {divergences.map(divergence => (
                        <DivergenceItem
                          key={`${divergence.field}-${divergence.code}`}
                          divergence={divergence}
                        />
                      ))}
                    </Stack>
                  </Alert>
                )}

                {consistencyStatus === 'valid' && divergences.length > 0 && (
                  <Alert
                    status="warning"
                    borderRadius="md"
                    mt={2}
                    alignItems="flex-start"
                  >
                    <AlertIcon />
                    <Stack gap={1} w="100%">
                      {divergences.map(divergence => (
                        <DivergenceItem
                          key={`${divergence.field}-${divergence.code}`}
                          divergence={divergence}
                        />
                      ))}
                    </Stack>
                  </Alert>
                )}

                {consistencyStatus === 'error' && (
                  <Alert
                    status="warning"
                    w="100%"
                    maxW="100%"
                    borderRadius="md"
                    mt={2}
                    alignItems="flex-start"
                  >
                    <AlertIcon />
                    <Stack gap={1}>
                      <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR}>
                        {consistencyErrorDetail
                          ? `${consistencyErrorDetail} ${THROTTLED_RETRY_HINT}`
                          : 'Não foi possível conferir o relatório de escolha agora. Isso não quer dizer que o arquivo esteja errado — você pode tentar de novo ou seguir com a solicitação, que o servidor faz a mesma conferência ao criar o processamento.'}
                      </Typography>
                      {!consistencyErrorDetail && (
                        <Box>
                          <Button
                            variant="tertiary"
                            label="Tentar novamente"
                            border="pill"
                            onClick={retryConsistency}
                            disabled={isSubmitting}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Alert>
                )}

                {serviceMapStatus === 'invalid' && (
                  <Alert
                    status="error"
                    borderRadius="md"
                    mt={2}
                    alignItems="flex-start"
                    data-testid="service-map-errors"
                  >
                    <AlertIcon />
                    <Stack gap={1} w="100%">
                      <Typography variant="s-sm" color={DIVERGENCE_TEXT_COLOR}>
                        O mapa de atendimento tem problemas e precisa ser
                        corrigido:
                      </Typography>
                      {serviceMapErrors.map((error, index) => (
                        <ServiceMapErrorItem
                          key={`${error.code}-${error.row ?? 'x'}-${index}`}
                          error={error}
                        />
                      ))}
                    </Stack>
                  </Alert>
                )}

                {serviceMapStatus === 'error' && (
                  <Alert
                    status="warning"
                    w="100%"
                    maxW="100%"
                    borderRadius="md"
                    mt={2}
                    alignItems="flex-start"
                    data-testid="service-map-check-failed"
                  >
                    <AlertIcon />
                    <Stack gap={1}>
                      <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR}>
                        {serviceMapErrorDetail
                          ? `${serviceMapErrorDetail} ${THROTTLED_RETRY_HINT}`
                          : 'Não foi possível conferir o mapa de atendimento agora. Tente novamente, anexe o arquivo de novo ou remova-o para seguir sem mapa.'}
                      </Typography>
                      {!serviceMapErrorDetail && (
                        <Box>
                          <Button
                            variant="tertiary"
                            label="Tentar novamente"
                            border="pill"
                            onClick={retryServiceMap}
                            disabled={isSubmitting}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Alert>
                )}

                {submitError && (
                  <Alert
                    status="error"
                    borderRadius="md"
                    mt={2}
                    data-testid="submit-error"
                  >
                    <AlertIcon />
                    <Typography variant="b-sm" color={DIVERGENCE_TEXT_COLOR}>
                      {submitError}
                    </Typography>
                  </Alert>
                )}

                {isUploading && (
                  <Stack gap={3} mt={2}>
                    <Stack gap={1}>
                      <Typography variant="b-sm">
                        Relatório de Adesão — {adherenceProgress}%
                      </Typography>
                      <Progress
                        value={adherenceProgress}
                        colorScheme={
                          adherenceProgress === 100 ? 'green' : 'blue'
                        }
                        hasStripe={adherenceProgress < 100}
                        isAnimated={adherenceProgress < 100}
                        borderRadius="full"
                        size="sm"
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            label="Cancelar"
            variant="secondary"
            border="square"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            label="Solicitar Processamento"
            variant="confirm"
            border="square"
            onClick={() => formikRef.current?.submitForm()}
            isLoading={isSubmitting}
            disabled={isSubmitting || !canSubmit || serviceMapBlocking}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default NewProcessingModal
